interface Modifier<T, U> {
  get(data: T): U | undefined;
  set(value: U, data: T): T;
  update: (fn: (value: U) => U, data: T) => T;
}

interface MultiComposeFunction {
  <T, U, V>(a: Modifier<T, U>, b: Modifier<U, V>): Modifier<T, V>;
  <T, U, V, W>(
    a: Modifier<T, U>,
    b: Modifier<U, V>,
    c: Modifier<V, W>
  ): Modifier<T, W>;
  <T, U, V, W, X>(
    a: Modifier<T, U>,
    b: Modifier<U, V>,
    c: Modifier<V, W>,
    d: Modifier<W, X>
  ): Modifier<T, X>;
  <T, U, V, W, X, Y>(
    a: Modifier<T, U>,
    b: Modifier<U, V>,
    c: Modifier<V, W>,
    d: Modifier<W, X>,
    e: Modifier<X, Y>
  ): Modifier<T, Y>;
  <T, U, V, W, X, Y, Z>(
    a: Modifier<T, U>,
    b: Modifier<U, V>,
    c: Modifier<V, W>,
    d: Modifier<W, X>,
    e: Modifier<X, Y>,
    f: Modifier<Y, Z>
  ): Modifier<T, Z>;
  <T, U, V, W, X, Y, Z, A>(
    a: Modifier<T, U>,
    b: Modifier<U, V>,
    c: Modifier<V, W>,
    d: Modifier<W, X>,
    e: Modifier<X, Y>,
    f: Modifier<Y, Z>,
    g: Modifier<Z, A>
  ): Modifier<T, A>;
  <T, U, V, W, X, Y, Z, A, B>(
    a: Modifier<T, U>,
    b: Modifier<U, V>,
    c: Modifier<V, W>,
    d: Modifier<W, X>,
    e: Modifier<X, Y>,
    f: Modifier<Y, Z>,
    g: Modifier<Z, A>,
    h: Modifier<A, B>
  ): Modifier<T, B>;
  <T, U, V, W, X, Y, Z, A, B, C>(
    a: Modifier<T, U>,
    b: Modifier<U, V>,
    c: Modifier<V, W>,
    d: Modifier<W, X>,
    e: Modifier<X, Y>,
    f: Modifier<Y, Z>,
    g: Modifier<Z, A>,
    h: Modifier<A, B>,
    i: Modifier<B, C>
  ): Modifier<T, C>;
  <T, U, V, W, X, Y, Z, A, B, C, D>(
    a: Modifier<T, U>,
    b: Modifier<U, V>,
    c: Modifier<V, W>,
    d: Modifier<W, X>,
    e: Modifier<X, Y>,
    f: Modifier<Y, Z>,
    g: Modifier<Z, A>,
    h: Modifier<A, B>,
    i: Modifier<B, C>,
    j: Modifier<C, D>
  ): Modifier<T, D>;
  <T, U, V, W, X, Y, Z, A, B, C, D, E>(
    a: Modifier<T, U>,
    b: Modifier<U, V>,
    c: Modifier<V, W>,
    d: Modifier<W, X>,
    e: Modifier<X, Y>,
    f: Modifier<Y, Z>,
    g: Modifier<Z, A>,
    h: Modifier<A, B>,
    i: Modifier<B, C>,
    j: Modifier<C, D>,
    k: Modifier<D, E>
  ): Modifier<T, E>;
  <T, U, V, W, X, Y, Z, A, B, C, D, E, F>(
    a: Modifier<T, U>,
    b: Modifier<U, V>,
    c: Modifier<V, W>,
    d: Modifier<W, X>,
    e: Modifier<X, Y>,
    f: Modifier<Y, Z>,
    g: Modifier<Z, A>,
    h: Modifier<A, B>,
    i: Modifier<B, C>,
    j: Modifier<C, D>,
    k: Modifier<D, E>,
    l: Modifier<E, F>
  ): Modifier<T, F>;
  <T, U, V, W, X, Y, Z, A, B, C, D, E, F, G>(
    a: Modifier<T, U>,
    b: Modifier<U, V>,
    c: Modifier<V, W>,
    d: Modifier<W, X>,
    e: Modifier<X, Y>,
    f: Modifier<Y, Z>,
    g: Modifier<Z, A>,
    h: Modifier<A, B>,
    i: Modifier<B, C>,
    j: Modifier<C, D>,
    k: Modifier<D, E>,
    l: Modifier<E, F>,
    m: Modifier<F, G>
  ): Modifier<T, G>;
  <T, U, V, W, X, Y, Z, A, B, C, D, E, F, G, H>(
    a: Modifier<T, U>,
    b: Modifier<U, V>,
    c: Modifier<V, W>,
    d: Modifier<W, X>,
    e: Modifier<X, Y>,
    f: Modifier<Y, Z>,
    g: Modifier<Z, A>,
    h: Modifier<A, B>,
    i: Modifier<B, C>,
    j: Modifier<C, D>,
    k: Modifier<D, E>,
    l: Modifier<E, F>,
    m: Modifier<F, G>,
    n: Modifier<G, H>
  ): Modifier<T, H>;
  <T, U, V, W, X, Y, Z, A, B, C, D, E, F, G, H, I>(
    a: Modifier<T, U>,
    b: Modifier<U, V>,
    c: Modifier<V, W>,
    d: Modifier<W, X>,
    e: Modifier<X, Y>,
    f: Modifier<Y, Z>,
    g: Modifier<Z, A>,
    h: Modifier<A, B>,
    i: Modifier<B, C>,
    j: Modifier<C, D>,
    k: Modifier<D, E>,
    l: Modifier<E, F>,
    m: Modifier<F, G>,
    n: Modifier<G, H>,
    o: Modifier<H, I>
  ): Modifier<T, I>;
  <T, U, V, W, X, Y, Z, A, B, C, D, E, F, G, H, I, J>(
    a: Modifier<T, U>,
    b: Modifier<U, V>,
    c: Modifier<V, W>,
    d: Modifier<W, X>,
    e: Modifier<X, Y>,
    f: Modifier<Y, Z>,
    g: Modifier<Z, A>,
    h: Modifier<A, B>,
    i: Modifier<B, C>,
    j: Modifier<C, D>,
    k: Modifier<D, E>,
    l: Modifier<E, F>,
    m: Modifier<F, G>,
    n: Modifier<G, H>,
    o: Modifier<H, I>,
    p: Modifier<I, J>
  ): Modifier<T, J>;
  <T, U, V, W, X, Y, Z, A, B, C, D, E, F, G, H, I, J, K>(
    a: Modifier<T, U>,
    b: Modifier<U, V>,
    c: Modifier<V, W>,
    d: Modifier<W, X>,
    e: Modifier<X, Y>,
    f: Modifier<Y, Z>,
    g: Modifier<Z, A>,
    h: Modifier<A, B>,
    i: Modifier<B, C>,
    j: Modifier<C, D>,
    k: Modifier<D, E>,
    l: Modifier<E, F>,
    m: Modifier<F, G>,
    n: Modifier<G, H>,
    o: Modifier<H, I>,
    p: Modifier<I, J>,
    q: Modifier<J, K>
  ): Modifier<T, K>;
  <T, U, V, W, X, Y, Z, A, B, C, D, E, F, G, H, I, J, K, L>(
    a: Modifier<T, U>,
    b: Modifier<U, V>,
    c: Modifier<V, W>,
    d: Modifier<W, X>,
    e: Modifier<X, Y>,
    f: Modifier<Y, Z>,
    g: Modifier<Z, A>,
    h: Modifier<A, B>,
    i: Modifier<B, C>,
    j: Modifier<C, D>,
    k: Modifier<D, E>,
    l: Modifier<E, F>,
    m: Modifier<F, G>,
    n: Modifier<G, H>,
    o: Modifier<H, I>,
    p: Modifier<I, J>,
    q: Modifier<J, K>,
    r: Modifier<K, L>
  ): Modifier<T, L>;
  <T, U, V, W, X, Y, Z, A, B, C, D, E, F, G, H, I, J, K, L, M>(
    a: Modifier<T, U>,
    b: Modifier<U, V>,
    c: Modifier<V, W>,
    d: Modifier<W, X>,
    e: Modifier<X, Y>,
    f: Modifier<Y, Z>,
    g: Modifier<Z, A>,
    h: Modifier<A, B>,
    i: Modifier<B, C>,
    j: Modifier<C, D>,
    k: Modifier<D, E>,
    l: Modifier<E, F>,
    m: Modifier<F, G>,
    n: Modifier<G, H>,
    o: Modifier<H, I>,
    p: Modifier<I, J>,
    q: Modifier<J, K>,
    r: Modifier<K, L>,
    s: Modifier<L, M>
  ): Modifier<T, M>;
  <T, U, V, W, X, Y, Z, A, B, C, D, E, F, G, H, I, J, K, L, M, N>(
    a: Modifier<T, U>,
    b: Modifier<U, V>,
    c: Modifier<V, W>,
    d: Modifier<W, X>,
    e: Modifier<X, Y>,
    f: Modifier<Y, Z>,
    g: Modifier<Z, A>,
    h: Modifier<A, B>,
    i: Modifier<B, C>,
    j: Modifier<C, D>,
    k: Modifier<D, E>,
    l: Modifier<E, F>,
    m: Modifier<F, G>,
    n: Modifier<G, H>,
    o: Modifier<H, I>,
    p: Modifier<I, J>,
    q: Modifier<J, K>,
    r: Modifier<K, L>,
    s: Modifier<L, M>,
    t: Modifier<M, N>
  ): Modifier<T, N>;
}

interface MultiComposeArrayFunction {
  <T, U, V>(a: Modifier<T, U[]>, b: Modifier<U, V>): Modifier<T, V[]>;
  <T, U, V, W>(
    a: Modifier<T, U[]>,
    b: Modifier<U, V[]>,
    c: Modifier<V, W>
  ): Modifier<T, W[]>;
  <T, U, V, W, X>(
    a: Modifier<T, U[]>,
    b: Modifier<U, V[]>,
    c: Modifier<V, W[]>,
    d: Modifier<W, X>
  ): Modifier<T, X[]>;
  <T, U, V, W, X, Y>(
    a: Modifier<T, U[]>,
    b: Modifier<U, V[]>,
    c: Modifier<V, W[]>,
    d: Modifier<W, X[]>,
    e: Modifier<X, Y>
  ): Modifier<T, Y[]>;
  <T, U, V, W, X, Y, Z>(
    a: Modifier<T, U[]>,
    b: Modifier<U, V[]>,
    c: Modifier<V, W[]>,
    d: Modifier<W, X[]>,
    e: Modifier<X, Y[]>,
    f: Modifier<Y, Z>
  ): Modifier<T, Z[]>;
  <T, U, V, W, X, Y, Z, A>(
    a: Modifier<T, U[]>,
    b: Modifier<U, V[]>,
    c: Modifier<V, W[]>,
    d: Modifier<W, X[]>,
    e: Modifier<X, Y[]>,
    f: Modifier<Y, Z[]>,
    g: Modifier<Z, A>
  ): Modifier<T, A[]>;
  <T, U, V, W, X, Y, Z, A, B>(
    a: Modifier<T, U[]>,
    b: Modifier<U, V[]>,
    c: Modifier<V, W[]>,
    d: Modifier<W, X[]>,
    e: Modifier<X, Y[]>,
    f: Modifier<Y, Z[]>,
    g: Modifier<Z, A[]>,
    h: Modifier<A, B>
  ): Modifier<T, B[]>;
  <T, U, V, W, X, Y, Z, A, B, C>(
    a: Modifier<T, U[]>,
    b: Modifier<U, V[]>,
    c: Modifier<V, W[]>,
    d: Modifier<W, X[]>,
    e: Modifier<X, Y[]>,
    f: Modifier<Y, Z[]>,
    g: Modifier<Z, A[]>,
    h: Modifier<A, B[]>,
    i: Modifier<B, C>
  ): Modifier<T, C[]>;
  <T, U, V, W, X, Y, Z, A, B, C, D>(
    a: Modifier<T, U[]>,
    b: Modifier<U, V[]>,
    c: Modifier<V, W[]>,
    d: Modifier<W, X[]>,
    e: Modifier<X, Y[]>,
    f: Modifier<Y, Z[]>,
    g: Modifier<Z, A[]>,
    h: Modifier<A, B[]>,
    i: Modifier<B, C[]>,
    j: Modifier<C, D>
  ): Modifier<T, D[]>;
  <T, U, V, W, X, Y, Z, A, B, C, D, E>(
    a: Modifier<T, U[]>,
    b: Modifier<U, V[]>,
    c: Modifier<V, W[]>,
    d: Modifier<W, X[]>,
    e: Modifier<X, Y[]>,
    f: Modifier<Y, Z[]>,
    g: Modifier<Z, A[]>,
    h: Modifier<A, B[]>,
    i: Modifier<B, C[]>,
    j: Modifier<C, D[]>,
    k: Modifier<D, E>
  ): Modifier<T, E[]>;
  <T, U, V, W, X, Y, Z, A, B, C, D, E, F>(
    a: Modifier<T, U[]>,
    b: Modifier<U, V[]>,
    c: Modifier<V, W[]>,
    d: Modifier<W, X[]>,
    e: Modifier<X, Y[]>,
    f: Modifier<Y, Z[]>,
    g: Modifier<Z, A[]>,
    h: Modifier<A, B[]>,
    i: Modifier<B, C[]>,
    j: Modifier<C, D[]>,
    k: Modifier<D, E[]>,
    l: Modifier<E, F>
  ): Modifier<T, F[]>;
  <T, U, V, W, X, Y, Z, A, B, C, D, E, F, G>(
    a: Modifier<T, U[]>,
    b: Modifier<U, V[]>,
    c: Modifier<V, W[]>,
    d: Modifier<W, X[]>,
    e: Modifier<X, Y[]>,
    f: Modifier<Y, Z[]>,
    g: Modifier<Z, A[]>,
    h: Modifier<A, B[]>,
    i: Modifier<B, C[]>,
    j: Modifier<C, D[]>,
    k: Modifier<D, E[]>,
    l: Modifier<E, F[]>,
    m: Modifier<F, G>
  ): Modifier<T, G[]>;
  <T, U, V, W, X, Y, Z, A, B, C, D, E, F, G, H>(
    a: Modifier<T, U[]>,
    b: Modifier<U, V[]>,
    c: Modifier<V, W[]>,
    d: Modifier<W, X[]>,
    e: Modifier<X, Y[]>,
    f: Modifier<Y, Z[]>,
    g: Modifier<Z, A[]>,
    h: Modifier<A, B[]>,
    i: Modifier<B, C[]>,
    j: Modifier<C, D[]>,
    k: Modifier<D, E[]>,
    l: Modifier<E, F[]>,
    m: Modifier<F, G[]>,
    n: Modifier<G, H>
  ): Modifier<T, H[]>;
  <T, U, V, W, X, Y, Z, A, B, C, D, E, F, G, H, I>(
    a: Modifier<T, U[]>,
    b: Modifier<U, V[]>,
    c: Modifier<V, W[]>,
    d: Modifier<W, X[]>,
    e: Modifier<X, Y[]>,
    f: Modifier<Y, Z[]>,
    g: Modifier<Z, A[]>,
    h: Modifier<A, B[]>,
    i: Modifier<B, C[]>,
    j: Modifier<C, D[]>,
    k: Modifier<D, E[]>,
    l: Modifier<E, F[]>,
    m: Modifier<F, G[]>,
    n: Modifier<G, H[]>,
    o: Modifier<H, I>
  ): Modifier<T, I[]>;
  <T, U, V, W, X, Y, Z, A, B, C, D, E, F, G, H, I, J>(
    a: Modifier<T, U[]>,
    b: Modifier<U, V[]>,
    c: Modifier<V, W[]>,
    d: Modifier<W, X[]>,
    e: Modifier<X, Y[]>,
    f: Modifier<Y, Z[]>,
    g: Modifier<Z, A[]>,
    h: Modifier<A, B[]>,
    i: Modifier<B, C[]>,
    j: Modifier<C, D[]>,
    k: Modifier<D, E[]>,
    l: Modifier<E, F[]>,
    m: Modifier<F, G[]>,
    n: Modifier<G, H[]>,
    o: Modifier<H, I[]>,
    p: Modifier<I, J>
  ): Modifier<T, J[]>;
  <T, U, V, W, X, Y, Z, A, B, C, D, E, F, G, H, I, J, K>(
    a: Modifier<T, U[]>,
    b: Modifier<U, V[]>,
    c: Modifier<V, W[]>,
    d: Modifier<W, X[]>,
    e: Modifier<X, Y[]>,
    f: Modifier<Y, Z[]>,
    g: Modifier<Z, A[]>,
    h: Modifier<A, B[]>,
    i: Modifier<B, C[]>,
    j: Modifier<C, D[]>,
    k: Modifier<D, E[]>,
    l: Modifier<E, F[]>,
    m: Modifier<F, G[]>,
    n: Modifier<G, H[]>,
    o: Modifier<H, I[]>,
    p: Modifier<I, J[]>,
    q: Modifier<J, K>
  ): Modifier<T, K[]>;
  <T, U, V, W, X, Y, Z, A, B, C, D, E, F, G, H, I, J, K, L>(
    a: Modifier<T, U[]>,
    b: Modifier<U, V[]>,
    c: Modifier<V, W[]>,
    d: Modifier<W, X[]>,
    e: Modifier<X, Y[]>,
    f: Modifier<Y, Z[]>,
    g: Modifier<Z, A[]>,
    h: Modifier<A, B[]>,
    i: Modifier<B, C[]>,
    j: Modifier<C, D[]>,
    k: Modifier<D, E[]>,
    l: Modifier<E, F[]>,
    m: Modifier<F, G[]>,
    n: Modifier<G, H[]>,
    o: Modifier<H, I[]>,
    p: Modifier<I, J[]>,
    q: Modifier<J, K[]>,
    r: Modifier<K, L>
  ): Modifier<T, L[]>;
  <T, U, V, W, X, Y, Z, A, B, C, D, E, F, G, H, I, J, K, L, M>(
    a: Modifier<T, U[]>,
    b: Modifier<U, V[]>,
    c: Modifier<V, W[]>,
    d: Modifier<W, X[]>,
    e: Modifier<X, Y[]>,
    f: Modifier<Y, Z[]>,
    g: Modifier<Z, A[]>,
    h: Modifier<A, B[]>,
    i: Modifier<B, C[]>,
    j: Modifier<C, D[]>,
    k: Modifier<D, E[]>,
    l: Modifier<E, F[]>,
    m: Modifier<F, G[]>,
    n: Modifier<G, H[]>,
    o: Modifier<H, I[]>,
    p: Modifier<I, J[]>,
    q: Modifier<J, K[]>,
    r: Modifier<K, L[]>,
    s: Modifier<L, M>
  ): Modifier<T, M[]>;
  <T, U, V, W, X, Y, Z, A, B, C, D, E, F, G, H, I, J, K, L, M, N>(
    a: Modifier<T, U[]>,
    b: Modifier<U, V[]>,
    c: Modifier<V, W[]>,
    d: Modifier<W, X[]>,
    e: Modifier<X, Y[]>,
    f: Modifier<Y, Z[]>,
    g: Modifier<Z, A[]>,
    h: Modifier<A, B[]>,
    i: Modifier<B, C[]>,
    j: Modifier<C, D[]>,
    k: Modifier<D, E[]>,
    l: Modifier<E, F[]>,
    m: Modifier<F, G[]>,
    n: Modifier<G, H[]>,
    o: Modifier<H, I[]>,
    p: Modifier<I, J[]>,
    q: Modifier<J, K[]>,
    r: Modifier<K, L[]>,
    s: Modifier<L, M[]>,
    t: Modifier<M, N>
  ): Modifier<T, N[]>;
}

interface ModifierFromPath<T> {
  <
    K1 extends keyof T,
    K2 extends keyof T[K1],
    K3 extends keyof T[K1][K2],
    K4 extends keyof T[K1][K2][K3],
    K5 extends keyof T[K1][K2][K3][K4],
    K6 extends keyof T[K1][K2][K3][K4][K5],
    K7 extends keyof T[K1][K2][K3][K4][K5][K6],
    K8 extends keyof T[K1][K2][K3][K4][K5][K6][K7],
    K9 extends keyof T[K1][K2][K3][K4][K5][K6][K7][K8],
    K10 extends keyof T[K1][K2][K3][K4][K5][K6][K7][K8][K9]
  >(
    path: [K1, K2, K3, K4, K5, K6, K7, K8, K9, K10]
  ): Modifier<T, T[K1][K2][K3][K4][K5][K6][K7][K8][K9][K10]>;
  <
    K1 extends keyof T,
    K2 extends keyof T[K1],
    K3 extends keyof T[K1][K2],
    K4 extends keyof T[K1][K2][K3],
    K5 extends keyof T[K1][K2][K3][K4],
    K6 extends keyof T[K1][K2][K3][K4][K5],
    K7 extends keyof T[K1][K2][K3][K4][K5][K6],
    K8 extends keyof T[K1][K2][K3][K4][K5][K6][K7],
    K9 extends keyof T[K1][K2][K3][K4][K5][K6][K7][K8]
  >(
    path: [K1, K2, K3, K4, K5, K6, K7, K8, K9]
  ): Modifier<T, T[K1][K2][K3][K4][K5][K6][K7][K8][K9]>;
  <
    K1 extends keyof T,
    K2 extends keyof T[K1],
    K3 extends keyof T[K1][K2],
    K4 extends keyof T[K1][K2][K3],
    K5 extends keyof T[K1][K2][K3][K4],
    K6 extends keyof T[K1][K2][K3][K4][K5],
    K7 extends keyof T[K1][K2][K3][K4][K5][K6],
    K8 extends keyof T[K1][K2][K3][K4][K5][K6][K7]
  >(
    path: [K1, K2, K3, K4, K5, K6, K7, K8]
  ): Modifier<T, T[K1][K2][K3][K4][K5][K6][K7][K8]>;
  <
    K1 extends keyof T,
    K2 extends keyof T[K1],
    K3 extends keyof T[K1][K2],
    K4 extends keyof T[K1][K2][K3],
    K5 extends keyof T[K1][K2][K3][K4],
    K6 extends keyof T[K1][K2][K3][K4][K5],
    K7 extends keyof T[K1][K2][K3][K4][K5][K6]
  >(
    path: [K1, K2, K3, K4, K5, K6, K7]
  ): Modifier<T, T[K1][K2][K3][K4][K5][K6][K7]>;
  <
    K1 extends keyof T,
    K2 extends keyof T[K1],
    K3 extends keyof T[K1][K2],
    K4 extends keyof T[K1][K2][K3],
    K5 extends keyof T[K1][K2][K3][K4],
    K6 extends keyof T[K1][K2][K3][K4][K5]
  >(
    path: [K1, K2, K3, K4, K5, K6]
  ): Modifier<T, T[K1][K2][K3][K4][K5][K6]>;
  <
    K1 extends keyof T,
    K2 extends keyof T[K1],
    K3 extends keyof T[K1][K2],
    K4 extends keyof T[K1][K2][K3],
    K5 extends keyof T[K1][K2][K3][K4]
  >(
    path: [K1, K2, K3, K4, K5]
  ): Modifier<T, T[K1][K2][K3][K4][K5]>;
  <
    K1 extends keyof T,
    K2 extends keyof T[K1],
    K3 extends keyof T[K1][K2],
    K4 extends keyof T[K1][K2][K3]
  >(
    path: [K1, K2, K3, K4]
  ): Modifier<T, T[K1][K2][K3][K4]>;
  <K1 extends keyof T, K2 extends keyof T[K1], K3 extends keyof T[K1][K2]>(
    path: [K1, K2, K3]
  ): Modifier<T, T[K1][K2][K3]>;
  <K1 extends keyof T, K2 extends keyof T[K1]>(path: [K1, K2]): Modifier<
    T,
    T[K1][K2]
  >;
  <K1 extends keyof T>(path: [K1]): Modifier<T, T[K1]>;
}

const compose = <T, U, V>(
  a: Modifier<T, U>,
  b: Modifier<U, V>
): Modifier<T, V> => ({
  get: (data: T) => (a.get(data) ? b.get(a.get(data)!) : undefined),
  set: (value: V, data: T) =>
    a.get(data) ? a.set(b.set(value, a.get(data)!), data) : data,
  update: (fn: (value: V) => V, data: T) =>
    a.get(data)
      ? b.get(a.get(data)!)
        ? a.set(b.set(fn(b.get(a.get(data)!)!), a.get(data)!), data)
        : data
      : data,
});

const getModifier = <T>() => {
  return <K extends keyof T>(key: K): Modifier<T, T[K]> => ({
    get: (data) => data[key],
    set: (value, data) =>
      Array.isArray(data)
        ? (data.map((v, i) => (i === key ? value : v)) as T)
        : { ...data, [key]: value },
    update: (fn, data) =>
      Array.isArray(data)
        ? (data.map((v, i) => (i === key ? fn(v) : v)) as T)
        : { ...data, [key]: fn(data[key]) },
  });
};

/**
 *
 * @description Build a factory function that returns a modifier from a path
 * @example
 * interface Address {
 *    city: string;
 *   street: string;
 * }
 *
 * interface User {
 *   name: string;
 *   age: number;
 *   address: Address[];
 * }
 *
 * const modifier = getModifierFromPath<User>()(['address', 0]);
 *
 * const user: User = {
 *   name: 'John',
 *   age: 30,
 *   address: [
 *     { city: 'Paris', street: 'Rue de la Paix' },
 *     { city: 'London', street: 'Baker Street' },
 *   ],
 * };
 *
 * const newUserData = modifier.set({ city: 'New York', street: '5th Avenue' }, user);
 * console.log(newUserData); // { name: 'John', age: 30, address: [ { city: 'New York', street: '5th Avenue' }, { city: 'London', street: 'Baker Street' } ] }
 *
 */
export const getModifierFromPath = <T>(): ModifierFromPath<T> =>
  ((path: Array<any>) => {
    return path
      .slice(1)
      .reduce(
        (acc, key) => compose(acc, getModifier<T>()(key)),
        getModifier<T>()(path[0])
      );
  }) as ModifierFromPath<T>;

/**
 *
 * @description Build a factory function that returns a modifier from a list of props
 * @example
 * interface User {
 *  name: string;
 *  age: number;
 *  address: string;
 * }
 *
 * const modifier = getModifierFromProps<User>()(['name', 'age']);
 *
 * const user: User = { name: 'Juana', age: 30, address: 'Calle Mayor' };
 *
 * const newUserData = modifier.set({ name: 'Evangelina', age: 32 }, user);
 * console.log(newUserData); // { name: 'Evangelina', age: 32, address: 'Calle Mayor' }
 */
export const getModifierFromProps =
  <T>() =>
  <P extends keyof T>(props: P[]): Modifier<T, { [K in P]: T[K] }> => ({
    get: (data) => {
      return props.reduce(
        (acc, prop) => ({ ...acc, [prop]: data[prop] }),
        {} as { [K in P]: T[K] }
      );
    },
    set: (value, data) => ({ ...data, ...value }),
    update: (fn, data) => ({ ...data, ...fn(data) }),
  });

/**
 *
 * @description Add an isomorphism to a modifier
 * @example
 * interface Measure {
 *  value: number;
 *  date: Date;
 * }
 *
 * const measureModifier = getModifierFromPath<Measure>()(['value']);
 *
 * const measure = { value: 10, unit: 'cm', date: new Date() };
 * const toMeters = (value: number) => value / 100;
 * const fromMeters = (value: number) => value * 100;
 *
 * const measureInMetersModifier = addIsomorphism(measureModifier, toMeters, fromMeters);
 *
 * const newMeasure = measureInMetersModifier.set(0.01, measure);
 * console.log(newMeasure); // { value: 1, unit: 'cm', date: Date }
 * console.log(measureInMetersModifier.get(newMeasure)); // 0.01
 */
export const addIsomorphism = <T, U, V>(
  modifier: Modifier<T, U>,
  to: (data: U) => V,
  from: (data: V) => U
): Modifier<T, V> => ({
  get: (data) => (modifier.get(data) ? to(modifier.get(data)!) : undefined),
  set: (value, data) => modifier.set(from(value), data),
  update: (fn, data) => modifier.update((value) => from(fn(to(value))), data),
});

/**
 *
 * @description Add a guard to a modifier
 * @example
 * interface ValueA {
 *  kind: 'A';
 *  value: boolean;
 * }
 *
 * interface ValueB {
 *  kind: 'B';
 *  value: string;
 *  count: number;
 * }
 *
 * type ValueAorB = ValueA | ValueB;
 *
 * const isValueB = (data: ValueAorB): data is ValueB => data.kind === 'B';
 * const valueBModifier = getModifierFromPath<ValueB>()(['count']); // Modifier<ValueB, number>
 * const valueAorBModifier = addGuard(isValueA, valueBModifier); // Modifier<ValueAorB, number>
 *
 * console.log(valueAorBModifier.set(10, { kind: 'A', value: true })); // { kind: 'A', value: true }
 * console.log(valueAorBModifier.set(10, { kind: 'B', value: 'hello', count: 5 })); // { kind: 'B', value: 'hello', count: 10 }
 */
export const addGuard = <T, U extends T, V>(
  guard: (data: T) => data is U,
  modifier: Modifier<U, V>
): Modifier<T, V> => ({
  get: (data) => (guard(data) ? modifier.get(data) : undefined),
  set: (value, data) => (guard(data) ? modifier.set(value, data) : data),
  update: (fn, data) => (guard(data) ? modifier.update(fn, data) : data),
});

/**
 *
 * @description Base modifier that does not modify the data. Used in combination with higher-order modifiers.
 */
export const idModifier = <T>(): Modifier<T, T> => ({
  get: (data) => data,
  set: (value, _) => value,
  update: (fn, data) => fn(data),
});

/**
 *
 * @description Add a guard and an isomorphism to a modifier
 * @example
 * interface ValueA {
 *  kind: 'A';
 *  value: boolean;
 * }
 *
 * interface ValueB {
 *  kind: 'B';
 *  value: string;
 *  count: number;
 * }
 *
 * type ValueAorB = ValueA | ValueB;
 *
 * const isValueB = (data: ValueAorB): data is ValueB => data.kind === 'B';
 * const valueBModifier = getModifierFromPath<ValueB>()(['count']); // Modifier<ValueB, number>
 * const toChar = (value: number) => String.fromCharCode(value);
 * const fromChar = (value: string) => value.charCodeAt(0);
 * const valueAorBModifier = addGuardAndIsomorphism(isValueA, valueBModifier, toChar, fromChar); // Modifier<ValueAorB, string>
 *
 * console.log(valueAorBModifier.set('g', { kind: 'A', value: true })); // { kind: 'A', value: true }
 * console.log(valueAorBModifier.set('g', { kind: 'B', value: 'hello', count: 5 })); // { kind: 'B', value: 'hello', count: 103 }
 * console.log(valueAorBModifier.get({ kind: 'B', value: 'hello', count: 103 })); // 'g'
 */
export const addGuardAndIsomorphism = <T, U extends T, V, W>(
  guard: (data: T) => data is U,
  modifier: Modifier<U, V>,
  to: (data: V) => W,
  from: (data: W) => V
): Modifier<T, W> => addIsomorphism(addGuard(guard, modifier), to, from);

export const multiCompose: MultiComposeFunction = (
  ...modifiers: Modifier<any, any>[]
) => {
  return modifiers.reduce(
    (acc, modifier) => compose(acc, modifier),
    idModifier()
  );
};

/**
 *
 * @description Compose two modifiers, where the first modifier modifies an array of elements
 * while the second modifier modifies the elements of the array
 * @example
 * interface Address {
 *  city: string;
 *  street: string;
 * }
 *
 * interface User {
 *  name: string;
 *  age: number;
 *  address: Address[];
 * }
 *
 * const modifier = composeArray(
 *  getModifierFromPath<User>()(['address']), // Modifier<User, Address[]>
 *  getModifierFromPath<Address>()(['city']), // Modifier<Address, string>
 * ); // Modifier<User, string[]>
 *
 * const user: User = {
 *  name: 'John',
 *  age: 30,
 *  address: [
 *   { city: 'Paris', street: 'Rue de la Paix' },
 *   { city: 'London', street: 'Baker Street' },
 *  ],
 * };
 *
 * console.log(modifier.get(user)); // ['Paris', 'London']
 * console.log(modifier.set(['New York', 'Los Angeles'], user)); // { name: 'John', age: 30, address: [ { city: 'New York', street: 'Rue de la Paix' }, { city: 'Los Angeles', street: 'Baker Street' } ] }
 */
export const composeArray = <T, U, V>(
  a: Modifier<T, U[]>,
  b: Modifier<U, V>
): Modifier<T, V[]> => ({
  get: (data) =>
    a.get(data)
      ? a
          .get(data)!
          .map(b.get)
          .filter((v) => v !== undefined)
      : undefined,
  set: (value, data) => {
    const currentUs = a.get(data);
    if (!currentUs) {
      return data;
    }
    const newUs = currentUs.map((u, i) => b.set(value[i]!, u));
    return a.set(newUs, data);
  },
  update: (fn, data) => {
    const currentUs = a.get(data);
    if (!currentUs) {
      return data;
    }
    const currentVs = currentUs.map(b.get);
    if (currentVs.some((v) => v === undefined)) {
      return data;
    }
    const newVs = fn(currentVs as V[]);
    const newUs = newVs.map((v, i) => b.set(v, currentUs[i]!));
    return a.set(newUs, data);
  },
});

const multiComposeArrayHelper = <T, U, V>(
  a: Modifier<T, U[]>,
  b: Modifier<U, V[]>
): Modifier<T, V[]> => ({
  get: (data) =>
    a.get(data)
      ? a
          .get(data)!
          .flatMap(b.get)
          .filter((v) => v !== undefined)
      : undefined,
  set: (value, data) => {
    const currentUs = a.get(data);
    if (!currentUs) {
      return data;
    }
    const lengthForEach = currentUs.map((u) => b.get(u)?.length || 0);
    const sliceBoundariesForEach = lengthForEach.reduce(
      (acc, length, index) => [
        ...acc,
        index === 0
          ? ([0, length] as [number, number])
          : ([acc[index - 1]![1], acc[index - 1]![1] + length] as [
              number,
              number
            ]),
      ],
      [] as [number, number][]
    );
    const newVss = sliceBoundariesForEach.map(([start, end]) =>
      value.slice(start, end)
    );
    const newUs = newVss.map((vs, i) => b.set(vs, currentUs[i]!));
    return a.set(newUs, data);
  },
  update: (fn, data) => {
    const currentUs = a.get(data);
    if (!currentUs) {
      return data;
    }
    const currentVss = currentUs.map(b.get);
    if (
      currentVss.some(
        (vs) => vs === undefined || vs.some((v) => v === undefined)
      )
    ) {
      return data;
    }
    const newVss = (currentVss as V[][]).map(fn);
    const newUs = newVss.map((vs, i) => b.set(vs, currentUs[i]!));
    return a.set(newUs, data);
  },
});

/**
 *
 * @description Compose multiple modifiers, where every modifier modifies an array of elements
 * except the last one, which modifies the elements of the array
 * @example
 * interface Address {
 *  city: string;
 *  street: string;
 * }
 *
 * interface Person {
 *  name: string;
 *  age: number;
 *  addresses: Address[];
 * }
 *
 * interface Team {
 *  name: string;
 *  members: Person[];
 * }
 *
 * const modifier = multiComposeArray(
 *  getModifierFromPath<Team>()(['members']), // Modifier<Team, Person[]>
 *  getModifierFromPath<Person>()(['addresses']), // Modifier<Person, Address[]>
 *  getModifierFromPath<Address>()(['city']), // Modifier<Address, string>
 * ); // Modifier<Team, string[]>
 *
 * const team: Team = {
 *  name: 'Team A',
 *  members: [
 *   { name: 'John', age: 30, addresses: [ { city: 'Paris', street: 'Rue de la Paix' }, { city: 'London', street: 'Baker Street' } ] },
 *   { name: 'Jane', age: 25, addresses: [ { city: 'New York', street: '5th Avenue' }, { city: 'Los Angeles', street: 'Sunset Boulevard' } ] },
 *  ],
 * };
 *
 * console.log(modifier.get(team)); // ['Paris', 'London', 'New York', 'Los Angeles']
 * console.log(modifier.set(['Madrid', 'Barcelona', 'Seville', 'Valencia'], team));
 * // {
 * //  name: 'Team A',
 * //  members: [
 * //   {
 * //     name: 'John',
 * //     age: 30,
 * //     addresses: [ { city: 'Madrid', street: 'Rue de la Paix' }, { city: 'Barcelona', street: 'Baker Street' } ]
 * //   },
 * //   {
 * //     name: 'Jane',
 * //     age: 25,
 * //     addresses: [ { city: 'Seville', street: '5th Avenue' }, { city: 'Valencia', street: 'Sunset Boulevard' } ]
 * //   },
 */
export const multiComposeArray: MultiComposeArrayFunction = (
  ...modifiers: Modifier<any, any>[]
) => {
  let acc = modifiers[0]!;
  modifiers.slice(1).forEach((modifier, i) => {
    if (i !== modifiers.length - 2) {
      acc = multiComposeArrayHelper(acc, modifier);
    } else {
      acc = composeArray(acc, modifier);
    }
  });
  return acc;
};

/**
 *
 * @description Add a trigger to a modifier
 * @example
 * interface Value {
 *  value: number;
 *  changeCount: number;
 * }
 *
 * const valueModifier = getModifierFromPath<Value>()(['value']);
 * const countModifier = getModifierFromPath<Value>()(['changeCount']);
 * const postChange = (updated: Value) => countModifier.update((count) => count + 1, updated);
 *
 * const valueModifierWithTrigger = addTrigger(valueModifier, postChange, (current) => current);
 *
 * const value = { value: 10, changeCount: 0 };
 *
 * const newValue = valueModifierWithTrigger.set(20, value);
 * console.log(newValue); // { value: 20, changeCount: 1 }
 */
export const addTrigger = <T, U>(
  modifier: Modifier<T, U>,
  postChange: (updated: T, changed: U) => T,
  preChange: (current: T, changed: U) => T
): Modifier<T, U> => ({
  get: (data) => modifier.get(data),
  set: (value, data) =>
    postChange(modifier.set(value, preChange(data, value)), value),
  update: (fn, data) =>
    modifier.get(data)
      ? postChange(
          modifier.update(fn, preChange(data, fn(modifier.get(data)!))),
          fn(modifier.get(data)!)
        )
      : data,
});
